import {ACTIVITY_TYPE, AllActivityTypes} from "../../types-enums/activity";
import {isActivityWithDistanceProps, isActivityWithReturnRouteProps} from "./type-guards";
import {ActivityWithDistanceProps, ActivityWithReturnRouteProps} from "../../types-enums/activity-common-properties";

/**
 * This function determines if the given Activity has a return route. If an Activity has a return route, certain things
 * must be handled differently. For i.e. flights, the return route must be calculated separately because load factors etc.
 * can be different on the return flight. The display in Travel Components Table and the way the Activity is changes its
 * state from Travel Component to Journey is also different.
 */
export const activityHasReturnOption = (activity: AllActivityTypes): activity is AllActivityTypes & ActivityWithReturnRouteProps => {
    const {common: {activityType}} = activity;
    switch (activityType) {
        case ACTIVITY_TYPE.hotel:
            return false
        case ACTIVITY_TYPE.flight:
            return isActivityWithReturnRouteProps(activity)
        case ACTIVITY_TYPE.car_bus:
            return false
        default:
            activityType satisfies never;
            return false;
    }
};

/**
 * This function determines, if the emissions of an Activity must be recalculated when the date of the
 * Activity changes. If a flight has a flight number and - date and the date of the flight changes (i.e.
 * via drag & drop in journey screen, the emissions for this flight must be recalculated.
 * If introducing a new Activity to the FE, set the boolean here and check wherever this function is
 * used and add your program logic accordingly.
 */
export const recalculateActivityOnDifferentDate = (activityType: ACTIVITY_TYPE) => {
    switch (activityType) {
        case ACTIVITY_TYPE.hotel:
            return false;
        case ACTIVITY_TYPE.flight:
            return true;
        case ACTIVITY_TYPE.car_bus:
            return false
        default:
            activityType satisfies never
    }
}

/**
 * This function determines, if the date of an Activity must be changed, if the day of the Activity changes.
 * Assuming there is a hotel stay at a specific date, and this hotel gets dragged & dropped in the journey screen
 * to a different day, then the date of the hotel stay must be updated too.
 * If introducing a new Activity to the FE, set the boolean here and check wherever this function is
 * used and add your program logic accordingly.
 */
export const changeDateOnDayChange = (activityType: ACTIVITY_TYPE) => {
    switch (activityType) {
        case ACTIVITY_TYPE.hotel:
            return true;
        case ACTIVITY_TYPE.flight:
            return false; // returns false because recalculateActivityOnDifferentDate returns already true
        case ACTIVITY_TYPE.car_bus:
            return false
        default:
            activityType satisfies never
    }
}

/**
 * This function determines, if the distance on an Activity must be changed (reduced down to distance per day),
 * if the Activity changes from the "Travel Component State" to "Journey State".
 * This is only relevant for Activities that can have a duration of more than one day AND contain a distance, like car/bus.

 */
export const adjustDistanceOnAddToDay = (activity: AllActivityTypes): activity is AllActivityTypes & ActivityWithDistanceProps => {
    const {common: {activityType}} = activity
    switch (activityType) {
        case ACTIVITY_TYPE.hotel:
            return false;
        case ACTIVITY_TYPE.flight:
            return isActivityWithDistanceProps(activity);
        case ACTIVITY_TYPE.car_bus:
            return true
        default:
            activityType satisfies never
            return false
    }
}


/**
 * This function determines, if the duration on an Activity must be changed, if the Activity changes from the
 * "Travel Component State" to "Journey State" in the create journey from travel components screen.
 * Set only to false here, if the Activity does have an exact date, like a hotel. Then you must set changeDateOnDayChange
 * to true. If the Activity has a duration but not an exact date like a car, set to true here and to false in changeDateOnDayChange.
 * If introducing a new Activity to the FE, set the boolean here and check wherever this function is
 * used and add your program logic accordingly.
 */
export const adjustDurationOnAddToDay = (activityType: ACTIVITY_TYPE) => {
    switch (activityType) {
        case ACTIVITY_TYPE.hotel:
            return true;
        case ACTIVITY_TYPE.flight:
            return false;
        case ACTIVITY_TYPE.car_bus:
            return true
        default:
            activityType satisfies never
    }
}

/**
 * This function determines if Activities must be grouped. See this function mergeTravelComponentsToJourneyElements()
 * for an explanation.
 * If introducing a new Activity to the FE, set the boolean here and check wherever this function is
 * used and add your program logic accordingly.
 */
export const groupActivities = (activityType: ACTIVITY_TYPE) => {
    switch (activityType) {
        case ACTIVITY_TYPE.hotel:
            return true;
        case ACTIVITY_TYPE.flight:
            return false;
        case ACTIVITY_TYPE.car_bus:
            return true
        default:
            activityType satisfies never
    }
}

/**
 * This function determines if the given activity needs information displayed on the next day, i.e. a hotel has the display
 * of the checkout on the next day.
 */
export const isActivityWithNextDayInformation = (activityType: ACTIVITY_TYPE) => {
    switch (activityType) {
        case ACTIVITY_TYPE.hotel:
            return true;
        case ACTIVITY_TYPE.flight:
            return false;
        case ACTIVITY_TYPE.car_bus:
            return false
        default:
            activityType satisfies never
    }
}

